import React, { createContext, useContext, useState, useEffect } from 'react';
import api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  // Impersonation state

  // Clear any stale View As header on fresh page load
  useEffect(() => { localStorage.removeItem('leadex_view_as_id'); localStorage.removeItem('leadex_view_as_type'); }, []);
  const [viewAsActive, setViewAsActive] = useState(false);
  const [viewAsName, setViewAsName] = useState('');
  const [originalUser, setOriginalUser] = useState(null);
  const [originalPermissions, setOriginalPermissions] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('leadex_token');
    if (token) {
      api.get('/auth/me')
        .then(res => {
          setUser(res.data.user);
          setPermissions(res.data.permissions);
          setTenant(res.data.tenant);
        })
        .catch(() => {
          localStorage.removeItem('leadex_token');
          localStorage.removeItem('leadex_user');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token, user, permissions, tenant } = res.data;
    localStorage.setItem('leadex_token', token);
    setUser(user);
    setPermissions(permissions);
    setTenant(tenant);
    return res.data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    localStorage.removeItem('leadex_token');
    localStorage.removeItem('leadex_user');
    setUser(null);
    setPermissions([]);
    setTenant(null);
    localStorage.removeItem('leadex_view_as_id');
    localStorage.removeItem('leadex_view_as_type');
    setViewAsActive(false);
    setViewAsName('');
    setOriginalUser(null);
    setOriginalPermissions([]);
  };

  // Simulate permissions based on role
  const permissionsForRole = (role) => {
    if (role === 'owner') return ['*'];
    if (role === 'manager') return [
      'page:dashboard',
      'page:calendar','page:contentcalendar','page:creatives','page:postcopy',
      'page:scheduling','page:monitoring','page:pipeline','page:assignments',
      'page:touchpoints','page:team','page:reallocation',
      'page:costs','page:kpi','page:performance',
      'page:clients','page:masterdata',
      'tab:contentcalendar:scripts','tab:contentcalendar:internal','tab:contentcalendar:client','tab:contentcalendar:approved',
      'tab:creatives:pending','tab:creatives:internal','tab:creatives:client','tab:creatives:approved',
      'tab:postcopy:draft','tab:postcopy:internal','tab:postcopy:client','tab:postcopy:approved',
      'tab:scheduling:ready','tab:scheduling:scheduled','tab:scheduling:posted',
      'tab:monitoring:active','tab:monitoring:closed',
    ];
    if (role === 'member') return [
      'page:dashboard',
      'page:calendar','page:contentcalendar','page:creatives','page:postcopy',
      'page:scheduling','page:monitoring','page:pipeline','page:assignments',
      'page:touchpoints','page:team','page:reallocation',
      'tab:contentcalendar:scripts','tab:contentcalendar:internal','tab:contentcalendar:client','tab:contentcalendar:approved',
      'tab:creatives:pending','tab:creatives:internal','tab:creatives:client','tab:creatives:approved',
      'tab:postcopy:draft','tab:postcopy:internal','tab:postcopy:client','tab:postcopy:approved',
      'tab:scheduling:ready','tab:scheduling:scheduled','tab:scheduling:posted',
      'tab:monitoring:active','tab:monitoring:closed',
    ];
    if (role === 'client') return [
      'page:creatives','page:contentcalendar',
      'tab:contentcalendar:client','tab:contentcalendar:approved',
      'tab:creatives:client','tab:creatives:approved',
      'tab:postcopy:client','tab:postcopy:approved',
    ];
    return [];
  };

  const startViewAs = (member) => {
    // Save original state
    setOriginalUser(user);
    setOriginalPermissions(permissions);
    // Switch to impersonated user
    const simulatedUser = {
      id: member.id,
      name: member.name,
      email: member.email,
      role: member.role || 'member',
      initials: member.initials,
      color: member.color,
      designation: member.designation,
    };
    setUser(simulatedUser);
    // Use custom_permissions if set on this member, else fall back to role defaults
    const perms = Array.isArray(member.custom_permissions)
      ? member.custom_permissions
      : permissionsForRole(member.role || 'member');
    setPermissions(perms);
    localStorage.setItem('leadex_view_as_id', member.id);
    localStorage.setItem('leadex_view_as_type', member.role === 'client' ? 'client' : 'user');
    setViewAsActive(true);
    setViewAsName(member.name);
  };

  const exitViewAs = () => {
    setUser(originalUser);
    setPermissions(originalPermissions);
    localStorage.removeItem('leadex_view_as_id');
    setViewAsActive(false);
    setViewAsName('');
    setOriginalUser(null);
    setOriginalPermissions([]);
  };

  /**
   * Check if the user has ANY level of access to a permission (full OR read-only).
   * Use for: sidebar visibility, showing pages, read-only data fetches.
   *
   * Phase 1: "page:calendar:read" satisfies can('page:calendar') — user sees the
   * page in sidebar but page components should check canWrite() before allowing edits.
   */
  const can = (permission) => {
    if (permissions.includes('*')) return true;
    return permissions.includes(permission) || permissions.includes(`${permission}:read`);
  };

  /**
   * Check if the user has FULL (write) access to a permission.
   * Returns false if the user only has read-only (":read") access.
   * Use for: showing/hiding add/edit/delete buttons inside pages.
   *
   * Usage: const { canWrite } = useAuth();
   *        {canWrite('page:calendar') && <button>Add</button>}
   */
  const canWrite = (permission) => {
    if (permissions.includes('*')) return true;
    return permissions.includes(permission); // NOT :read variant
  };

  /**
   * Check if user can access a specific sub-tab.
   * Backward-compatible: if no tab permissions exist for this page at all,
   * returns true (all tabs visible). Only filters when at least one
   * tab permission for that page is present in the user's permissions.
   */
  const canTab = (tabPermissionKey) => {
    if (permissions.includes('*')) return true;
    if (!tabPermissionKey) return true;
    // Extract page key: "tab:contentcalendar:scripts" → "tab:contentcalendar:"
    const parts = tabPermissionKey.split(':');
    const prefix = parts.slice(0, 2).join(':') + ':';
    // Check if user has ANY tab permission for this page
    const hasAnyTabPerm = permissions.some(p => p.startsWith(prefix) || p.startsWith(prefix.replace(':',':') + ':read'));
    if (!hasAnyTabPerm) return true; // No tab perms at all → backward compat, show all
    return permissions.includes(tabPermissionKey) || permissions.includes(tabPermissionKey + ':read');
  };

  return (
    <AuthContext.Provider value={{
      user, permissions, tenant, loading, login, logout, can, canWrite, canTab,
      viewAsActive, viewAsName, startViewAs, exitViewAs,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}